// Pure decision logic for the GST-mismatch flow (W4b): the org HAS a zoho_contact_id,
// but the GST we hold and the GST Zoho holds disagree. Kept free of React so every rule
// below is unit-testable on its own.
//
// Sibling of zoho_gst_match.js, which handles the *other* case — no contact id at all,
// resolve one from the GST. Same button, different precondition.

import { normalise_gst } from './zoho_gst_match';
import { is_valid_gst } from './gst_validation';

/**
 * Find the org's own contact in the dump by contact id (NOT by GST — here we already
 * know which contact is ours; the question is only what GST it carries).
 */
export const find_contact_in_dump = (dump, zoho_contact_id) => {
	if (!Array.isArray(dump)) return null;
	const target = (zoho_contact_id === null || zoho_contact_id === undefined)
		? '' : String(zoho_contact_id).trim();
	if (!target) return null;
	return dump.find(row => row && String(row.zoho_contact_id).trim() === target) || null;
};

/**
 * Should the mismatch modal open at all?
 *
 * Every negative answer means "today's behaviour, unchanged" — this feature gates a
 * revenue-path button, so anything uncertain must fall through to a plain download
 * rather than block it. Returns a reason so the caller can log why it stayed silent.
 *
 * Deliberately NOT a reason to open:
 *  - no contact id            -> that is the other modal's job
 *  - dump missing/unavailable -> we cannot know there is a mismatch
 *  - contact absent from dump -> stale dump or inactive contact; nothing to compare
 *  - GSTs agree               -> nothing to reconcile
 *  - backend GST invalid      -> per the plan, we do not interrupt on a value we
 *                                cannot vouch for; Accounts cleans those up in bulk
 *  - backend GST blank        -> nothing to compare against, and a blank must never
 *                                match the dump rows whose zoho_gst is itself blank
 */
export const derive_mismatch_state = ({ store_info, dump, dump_unavailable, market }) => {
	const zoho_contact_id = store_info && store_info.zoho_contact_id;
	if (!zoho_contact_id) return { should_prompt: false, reason: 'no_contact_id' };
	if (dump_unavailable) return { should_prompt: false, reason: 'dump_unavailable' };

	const backend_gst = store_info && store_info.gst_number ? store_info.gst_number : '';
	if (!normalise_gst(backend_gst)) return { should_prompt: false, reason: 'no_backend_gst' };

	const contact = find_contact_in_dump(dump, zoho_contact_id);
	if (!contact) return { should_prompt: false, reason: 'contact_not_in_dump' };

	const zoho_gst = contact.zoho_gst ? contact.zoho_gst : '';
	if (!normalise_gst(zoho_gst)) return { should_prompt: false, reason: 'no_zoho_gst' };

	if (normalise_gst(backend_gst) === normalise_gst(zoho_gst)) {
		return { should_prompt: false, reason: 'gst_match' };
	}
	// The plan's gate: only interrupt when the value we hold is one we can vouch for.
	if (!is_valid_gst(backend_gst, { market })) {
		return { should_prompt: false, reason: 'backend_gst_invalid' };
	}
	return {
		should_prompt: true,
		reason: 'mismatch',
		backend_gst: normalise_gst(backend_gst),
		zoho_gst: normalise_gst(zoho_gst),
		zoho_name: contact.zoho_name || '',
	};
};

/**
 * Where should the "Download Estimated Invoice" click go?
 *
 * Two different Zoho problems can block a correct estimate and they are mutually
 * exclusive, so the button routes between three outcomes:
 *   'resolve_contact' -> no contact id; resolve one from the GST (ZohoDetailsMissingModal)
 *   'resolve_mismatch'-> contact id present, GSTs disagree (ZohoGstMismatchModal)
 *   'download'        -> everything else, i.e. today's behaviour unchanged
 *
 * Extracted from the component so the routing itself is testable: this decides whether
 * a revenue-path button gets interrupted, which is the highest-consequence rule here.
 */
export const derive_download_route = ({ store_info, dump, dump_unavailable, market }) => {
	if (!(store_info && store_info.zoho_contact_id)) {
		return { route: 'resolve_contact', reason: 'no_contact_id' };
	}
	const state = derive_mismatch_state({ store_info, dump, dump_unavailable, market });
	if (state.should_prompt) return { route: 'resolve_mismatch', reason: state.reason, mismatch: state };
	return { route: 'download', reason: state.reason };
};

/**
 * Which sides does csp/finalise_org_gst need to write, given the value the CS user
 * settled on?
 *
 * A side already holding the chosen value is left alone: asking the endpoint to
 * rewrite it would produce a pointless audit row, and for Zoho a pointless API call.
 *
 * STALENESS: `zoho_gst` here comes from the weekly contacts dump, NOT from a live read
 * of Zoho — there is no endpoint that fetches a single contact's GST on demand, so the
 * dump is the only Zoho value available at this point. `backend_gst` IS live; it comes
 * from store_info. Two consequences, both acceptable:
 *  - Dump stale, Zoho already holds the chosen value -> we ask for a Zoho write that is
 *    a no-op. Harmless.
 *  - Dump stale the other way (Zoho has since moved OFF the dumped value, and the dumped
 *    value happens to be what the user chose) -> we skip the Zoho write and the two
 *    systems stay divergent. This is the residual gap. It is not silent: the org shows up
 *    in the next dump as a mismatch and the modal opens again on the next download.
 */
export const derive_sides_to_update = (chosen_gst, { backend_gst, zoho_gst }) => {
	const chosen = normalise_gst(chosen_gst);
	return {
		update_backend: chosen !== normalise_gst(backend_gst),
		update_zoho: chosen !== normalise_gst(zoho_gst),
	};
};

/**
 * Body for csp/finalise_org_gst.
 *
 * The endpoint rejects customer_store_id / order_id / gst_number as non-strings with
 * INVALID_PARAMS, and rejects both flags false with INVALID_PARAMS, so every one of
 * those rules is enforced here at the single place the body is built. Returns null
 * when there is nothing to do, so the caller skips the call rather than sending a
 * request it knows will 400.
 */
export const build_finalise_body = (order_id, store_id, chosen_gst, sides) => {
	const { update_backend, update_zoho } = sides || {};
	if (!update_backend && !update_zoho) return null;
	return {
		customer_store_id: String(store_id),
		order_id: String(order_id),
		gst_number: String(chosen_gst),
		update_backend: Boolean(update_backend),
		update_zoho: Boolean(update_zoho),
	};
};

/**
 * Turn a finalise response/error into what the CS user should be told.
 *
 * The endpoint reports each side separately and THROWS on a one-sided result, with the
 * per-side outcome in a `gst_finalise` field on the response body (the route cannot use
 * the shared error registry for this — mutating it would leak one user's GST into
 * another's toast). So a partial failure arrives as an error carrying real data, and
 * must be reported as "one side landed", never as blanket success or blanket failure.
 */
export const describe_finalise_outcome = (details) => {
	const backend = Boolean(details && details.backend_updated);
	const zoho = Boolean(details && details.zoho_updated);
	if (backend && zoho) return { ok: true, message: 'GST updated on Infurnia and Zoho.' };
	if (backend) return { ok: false, message: 'GST updated on Infurnia, but the Zoho update failed. Please retry.' };
	if (zoho) return { ok: false, message: 'GST updated on Zoho, but the Infurnia update failed. Please retry.' };
	return { ok: false, message: 'Could not update the GST. Please try again.' };
};

// general_fetch rejects with {err, resp}: `resp` is the parsed body, and `err.message`
// is that same body stringified into the Error text. Prefer the structured code and fall
// back to the substring match, which is the established idiom in this repo
// (see is_dump_unavailable_error and components/EditTdsModal/index.jsx:150).
const error_code_of = (err) => {
	if (err && err.resp && err.resp.code) return String(err.resp.code);
	return (err && err.err && err.err.message) ? String(err.err.message) : '';
};

/**
 * Extract the per-side detail the route puts on a partial failure. Returns null when
 * the error is not a partial failure, so the caller can fall back to a generic toast.
 *
 * The shape is dictated by general_fetch, which rejects with `{err, resp, info}` where
 * `resp` is the PARSED RESPONSE BODY (src/utils/fetch.js). The route puts the per-side
 * outcome in a top-level `gst_finalise` field on that body, so it arrives at
 * `err.resp.gst_finalise` — not on the Error object itself. Getting this wrong silently
 * discards the whole point of the endpoint's partial-failure contract and degrades every
 * one-sided result to a generic "please try again".
 */
export const extract_finalise_details = (err) => {
	if (err && err.resp && err.resp.gst_finalise) return err.resp.gst_finalise;
	return null;
};

/**
 * A GST the endpoint refused. Distinguished from a transport failure because the CS
 * user can fix this one by typing a different value.
 */
export const is_invalid_gst_error = (err) => error_code_of(err).includes('INVALID_GST_NUMBER');
